import { useMemo, useState } from 'react'
import { CheckCircle2, Network, Upload } from 'lucide-react'

import api from '@/services/api'

export type OrgChartRow = {
  email: string
  manager_email?: string | null
  department?: string | null
  team?: string | null
  job_title?: string | null
}

type ImportRowResult = {
  row: number
  email: string
  status: 'unchanged' | 'update' | 'unknown-account' | 'unknown-manager' | 'rejected'
  note?: string
  changes?: Record<string, { from: string | null; to: string | null }>
}

type ImportResult = {
  committed: boolean
  updates?: number
  updated?: number
  problems?: ImportRowResult[]
  rows: ImportRowResult[]
  note?: string
}

const COLUMNS = ['email', 'manager_email', 'department', 'team', 'job_title'] as const
const EMPTY_TEMPLATE = 'email,manager_email,department,team,job_title\n'

function csvCells(line: string): string[] {
  const cells: string[] = []
  let current = ''
  let quoted = false
  for (let index = 0; index < line.length; index += 1) {
    const char = line[index]
    if (char === '"') {
      if (quoted && line[index + 1] === '"') {
        current += '"'
        index += 1
      } else {
        quoted = !quoted
      }
    } else if (char === ',' && !quoted) {
      cells.push(current.trim())
      current = ''
    } else {
      current += char
    }
  }
  cells.push(current.trim())
  return cells
}

export function parseOrgChartRows(value: string): OrgChartRow[] {
  const lines = value.split(/\r?\n/).map((line) => line.trim()).filter(Boolean)
  if (lines.length === 0) return []
  const first = csvCells(lines[0]).map((cell) => cell.toLowerCase().replace(/ /g, '_'))
  const hasHeader = first.includes('email')
  const header = hasHeader ? first : [...COLUMNS]
  const dataLines = hasHeader ? lines.slice(1) : lines
  const emailIndex = header.indexOf('email')
  if (emailIndex < 0) throw new Error('The roster needs an email column.')

  return dataLines.map((line, rowIndex) => {
    const cells = csvCells(line)
    const read = (name: (typeof COLUMNS)[number]) => {
      const index = header.indexOf(name)
      return index >= 0 ? cells[index]?.trim() || null : null
    }
    const email = cells[emailIndex]?.trim().toLowerCase()
    if (!email) throw new Error(`Row ${rowIndex + 1} is missing an email address.`)
    return {
      email,
      manager_email: read('manager_email')?.toLowerCase() ?? null,
      department: read('department'),
      team: read('team'),
      job_title: read('job_title'),
    }
  })
}

export default function OrgChartSettingsPanel() {
  const [source, setSource] = useState(EMPTY_TEMPLATE)
  const [preview, setPreview] = useState<ImportResult | null>(null)
  const [previewKey, setPreviewKey] = useState('')
  const [busy, setBusy] = useState<'preview' | 'commit' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const parsed = useMemo(() => {
    try {
      return { rows: parseOrgChartRows(source), error: null }
    } catch (caught) {
      return { rows: [] as OrgChartRow[], error: (caught as Error).message }
    }
  }, [source])
  const sourceKey = JSON.stringify(parsed.rows)
  const problems = preview?.problems ?? preview?.rows.filter((row) => (
    row.status === 'unknown-account' || row.status === 'unknown-manager' || row.status === 'rejected'
  )) ?? []
  const previewIsCurrent = Boolean(preview && previewKey === sourceKey)

  const submit = async (commit: boolean) => {
    if (parsed.error || parsed.rows.length === 0) return
    setBusy(commit ? 'commit' : 'preview')
    setError(null)
    try {
      const response = await api.post<ImportResult>('/org-chart/import', {
        rows: parsed.rows,
        commit,
      })
      setPreview(response.data)
      setPreviewKey(sourceKey)
    } catch (caught) {
      const detail = (caught as { response?: { data?: { detail?: unknown } } }).response?.data?.detail
      setError(typeof detail === 'string' ? detail : 'The reporting-line import failed.')
    } finally {
      setBusy(null)
    }
  }

  return (
    <section className="space-y-4 border-t border-amber-400/10 pt-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2"><Network size={14} className="text-amber-400" /><h3 className="text-[13px] font-medium text-stone-100">Reporting line</h3></div>
          <p className="mt-1 text-[11px] text-stone-500">Paste Antarctica&apos;s roster, preview every access-affecting change, then commit the reviewed result.</p>
        </div>
        <span className="text-[10px] text-stone-600">No accounts are created by this import</span>
      </div>

      <textarea
        aria-label="Reporting line roster"
        className="min-h-32 w-full rounded-lg border border-amber-400/15 bg-black/40 p-3 font-mono text-[11px] leading-relaxed text-stone-300 outline-none focus:border-amber-400/35"
        value={source}
        onChange={(event) => {
          setSource(event.target.value)
          setPreview(null)
          setError(null)
        }}
        spellCheck={false}
      />
      <div className="flex items-center gap-2">
        <button type="button" className="btn-secondary flex items-center gap-1.5 text-[11px]" disabled={busy !== null || parsed.rows.length === 0 || Boolean(parsed.error)} onClick={() => void submit(false)}><Upload size={12} />{busy === 'preview' ? 'Previewing…' : 'Preview changes'}</button>
        <button type="button" className="btn-primary flex items-center gap-1.5 text-[11px]" disabled={busy !== null || !previewIsCurrent || problems.length > 0 || preview?.committed === true} onClick={() => void submit(true)}><CheckCircle2 size={12} />{busy === 'commit' ? 'Committing…' : 'Commit reviewed roster'}</button>
        <span className="text-[10px] text-stone-600">{parsed.rows.length} roster rows parsed</span>
      </div>
      {(parsed.error || error) ? <div className="rounded border border-red-400/20 bg-red-400/[0.04] px-3 py-2 text-[11px] text-red-300">{parsed.error || error}</div> : null}

      {preview ? (
        <div className="overflow-hidden rounded-lg border border-amber-400/10">
          <div className="flex items-center justify-between border-b border-amber-400/10 bg-black/30 px-3 py-2 text-[10px] text-stone-500">
            <span>{preview.committed ? `${preview.updated ?? 0} reporting lines committed` : `${preview.updates ?? 0} changes ready for review`}</span>
            <span className={problems.length ? 'text-red-300' : 'text-emerald-300'}>{problems.length ? `${problems.length} problems must be fixed` : 'Validation passed'}</span>
          </div>
          <table className="dense-table">
            <thead><tr><th>Email</th><th>Status</th><th>Detail</th></tr></thead>
            <tbody>
              {preview.rows.map((row) => (
                <tr key={`${row.row}-${row.email}`}>
                  <td className="text-stone-300">{row.email}</td>
                  <td className={row.status === 'update' || row.status === 'unchanged' ? 'text-emerald-300' : 'text-red-300'}>{row.status}</td>
                  <td className="text-stone-500">{row.note || Object.keys(row.changes ?? {}).join(', ') || 'No changes'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  )
}
